import type { AdminSearchResultItem } from '../types';
import { GuideSubTypes } from '../constants';

const MAXIMUM_DESCRIPTION_SIZE = 50;
const MAX_SUBTITLE_SIZE = 300;

export const getDefaultTitle = (item: AdminSearchResultItem) => {
  if (item.type === GuideSubTypes.SECTION || item.type === GuideSubTypes.TASK) {
    const typeText = item.type === GuideSubTypes.SECTION ? 'Section' : 'Task';
    return `Untitled ${typeText}`;
  }

  return `Untitled ${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`;
};

export const truncateText = (text: string, length: number = MAXIMUM_DESCRIPTION_SIZE): string => {
  if (!text) {
    return '';
  }

  return text.length > length ? `${text.substring(0, length)}...` : text;
};

export const getSearchBarResultTitle = (item: AdminSearchResultItem) => {
  let title = null;

  if (item.type === GuideSubTypes.SECTION || item.type === GuideSubTypes.TASK) {
    title = item.guide.name;
  } else {
    title = item.snippetMatch?.title || item.name;
  }

  return title || getDefaultTitle(item);
};

export const getSearchBarResultSubtitle = (item: AdminSearchResultItem, maxLength: number = MAX_SUBTITLE_SIZE) => {
  let subtitle = null;
  if (item.type === GuideSubTypes.SECTION || item.type === GuideSubTypes.TASK) {
    subtitle =
      item.snippetMatch?.title ||
      getSnippetWithContext(item.snippetMatch?.subtitle, maxLength) ||
      item.name ||
      getDefaultTitle(item);
  } else {
    subtitle = getSnippetWithContext(item.snippetMatch?.subtitle, maxLength);
  }

  return truncateText(subtitle, maxLength);
};

/**
 * Generates a text snippet that is within the maximum length while preserving context around highlighted terms.
 * If the input text contains <strong> tags (which indicate highlighted search term), the function attempts to
 * place the highlighted term near the beginning of the snippet and show context around it to provide
 * better context for search results.
 *
 * Edge cases handled:
 * - Returns null if input text is null or undefined
 * - Returns the original text if it's already shorter than maxLength
 * - Returns the original text if no <strong> tags are found (no search matches within the input text)
 * - If the highlighted term is near the beginning or end, adjusts the snippet to show more context around it
 *
 * @param text - The text snippet to process, which may contain HTML <strong> tags for highlighting search matches
 * @param maxLength - Maximum allowed length for the returned snippet (defaults to MAX_SUBTITLE_SIZE)
 * @returns The processed snippet text or null if input is null/undefined
 */
export const getSnippetWithContext = (
  text: string | null | undefined,
  maxLength: number = MAX_SUBTITLE_SIZE,
): string | null => {
  if (text === null || text === undefined) {
    return null;
  }

  const snippetText = text;

  if (snippetText.length <= maxLength) {
    return snippetText;
  }

  // Find the first <strong> tag to determine where the matched word is
  const strongMatch = snippetText.match(/<strong[^>]*>.*?<\/strong>/i);

  if (!strongMatch || strongMatch.index === undefined) {
    // No matched words found, return text as is
    return snippetText;
  }

  // Extract match token and its boundaries
  const startTagIndex = strongMatch.index;
  const endTagIndex = startTagIndex + strongMatch[0].length;
  const matchToken = strongMatch[0];

  // Split text before and after the match token and tokenize by whitespace
  const frontText = snippetText.substring(0, startTagIndex);
  const backText = snippetText.substring(endTagIndex);

  // Split by whitespace but keep words with their trailing spaces for better reconstruction
  const frontTokens = frontText.split(/\s+/).filter((token) => token.length > 0);
  const backTokens = backText.split(/\s+/).filter((token) => token.length > 0);

  // Phase 1: Start with match token and fill with back tokens
  const selectedFrontTokens: string[] = [];
  const selectedBackTokens: string[] = [];
  let currentLength = matchToken.length;

  // Add as many back tokens as possible
  for (let i = 0; i < backTokens.length; i += 1) {
    const spaceLength = selectedBackTokens.length > 0 ? 1 : 0; // Add space if not first token
    if (currentLength + backTokens[i].length + spaceLength <= maxLength) {
      selectedBackTokens.push(backTokens[i]);
      currentLength += backTokens[i].length + spaceLength;
    } else {
      break;
    }
  }

  // Phase 2: Length-aware swapping and front token addition (max 3 swaps)
  const maxSwaps = 3;
  let swapCount = 0;
  let frontTokenIndex = frontTokens.length - 1; // Start from token closest to match

  while (frontTokenIndex >= 0 && swapCount < maxSwaps) {
    const frontToken = frontTokens[frontTokenIndex];

    if (selectedBackTokens.length > 0) {
      // Existing swap logic when back tokens are available
      const lastBackToken = selectedBackTokens[selectedBackTokens.length - 1];

      // Calculate new length after swap (account for space before front token)
      const frontSpaceLength = selectedFrontTokens.length > 0 ? 1 : 0;
      const backSpaceLength = 1; // Space that will be removed with back token
      const newLength = currentLength - lastBackToken.length - backSpaceLength + frontToken.length + frontSpaceLength;

      // Only swap if new length fits within maxLength
      if (newLength <= maxLength) {
        // Remove last back token
        selectedBackTokens.pop();
        currentLength -= lastBackToken.length + backSpaceLength;

        // Add front token at the beginning
        selectedFrontTokens.unshift(frontToken);
        currentLength += frontToken.length + frontSpaceLength;

        swapCount += 1;
      } else {
        // If this swap doesn't fit, stop trying (no point checking shorter front tokens)
        break;
      }
    } else {
      // Just add front token without swapping when no back tokens available
      const frontSpaceLength = selectedFrontTokens.length > 0 ? 1 : 0;
      if (currentLength + frontToken.length + frontSpaceLength <= maxLength) {
        selectedFrontTokens.unshift(frontToken);
        currentLength += frontToken.length + frontSpaceLength;
        swapCount += 1;
      } else {
        // If adding this front token exceeds maxLength, stop
        break;
      }
    }

    frontTokenIndex -= 1;
  }

  // Join the selected tokens back together with spaces and trim
  const frontPart = selectedFrontTokens.length > 0 ? `${selectedFrontTokens.join(' ')} ` : '';
  const backPart = selectedBackTokens.length > 0 ? ` ${selectedBackTokens.join(' ')}` : '';
  const result = (frontPart + matchToken + backPart).trim();

  // Ensure the result still contains the strong match
  if (!result.includes(strongMatch[0])) {
    return snippetText;
  }

  return result;
};
